import axios from 'axios';
import { INBOX_API_BASE_URL } from './config';
import api from './auth';

const API_BASE =
  (INBOX_API_BASE_URL || 'http://localhost:5000').replace(/\/+$/, '');

const inboxApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  // IMPORTANT:
  // On désactive l'envoi des credentials/cookies pour éviter le blocage CORS
  // quand le backend renvoie `Access-Control-Allow-Origin: *`.
  // (Ce module "inbox" ne doit pas dépendre du cookie auth.)
  withCredentials: false,
});

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.status ||
    err?.message ||
    'Erreur inconnue'
  );
}

export async function fetchBrands() {
  try {
    const resp = await inboxApi.get('/api/brands');
    return resp.data?.brands || [];
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function fetchConversations({ channel, brand, limit }) {
  try {
    const params = new URLSearchParams();
    if (channel) params.set('channel', channel);
    if (brand) params.set('brand', brand);
    if (limit) params.set('limit', String(limit));

    const query = params.toString();
    const url = query ? `/api/inbox/conversations?${query}` : '/api/inbox/conversations';
    const resp = await inboxApi.get(url);
    return resp.data?.conversations || [];
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function fetchConversationMessages({ conversationId, limit }) {
  try {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));

    const query = params.toString();
    const url = query
      ? `/api/inbox/conversations/${encodeURIComponent(conversationId)}/messages?${query}`
      : `/api/inbox/conversations/${encodeURIComponent(conversationId)}/messages`;

    const resp = await inboxApi.get(url);
    const messages = resp.data?.messages || [];
    return {
      messages,
      hasMore: messages.length >= (limit || 50),
    };
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function markConversationAsRead(conversationId) {
  try {
    const resp = await inboxApi.post(
      `/api/inbox/conversations/${encodeURIComponent(conversationId)}/read`,
      {}
    );
    return resp.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function sendOutboundMessage({
  channel,
  brand,
  userId,
  message,
  imageUrl,
  fileUrl,
  fileName,
  templateName,
  templateLanguage,
  templateComponents,
  contactName,
  contactPayload,
}) {
  try {
    const payload = {
      channel,
      brand,
      userId,
      message,
    };
    if (imageUrl) payload.imageUrl = imageUrl;
    if (fileUrl) payload.fileUrl = fileUrl;
    if (fileName) payload.fileName = fileName;
    if (templateName) payload.templateName = templateName;
    if (templateLanguage) payload.templateLanguage = templateLanguage;
    if (templateComponents) payload.templateComponents = templateComponents;
    if (contactName) payload.contactName = contactName;
    if (contactPayload) payload.contactPayload = contactPayload;

    const resp = await inboxApi.post('/api/inbox/reply', payload);
    return resp.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function uploadConversationMedia(file) {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const resp = await inboxApi.post('/api/inbox/upload-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function fetchWhatsappManagerUrl({ brand, templateName, metaTemplateId, searchText } = {}) {
  try {
    const params = new URLSearchParams();
    if (brand) params.set('brand', brand);
    if (templateName) params.set('template_name', templateName);
    if (metaTemplateId) params.set('meta_template_id', metaTemplateId);
    if (searchText) params.set('search_text', searchText);
    const query = params.toString();
    const url = query
      ? `/api/inbox/whatsapp/manager-url?${query}`
      : '/api/inbox/whatsapp/manager-url';
    const resp = await inboxApi.get(url);
    return resp.data || {};
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

const META_TEMPLATE_TITLE_RE = /^(?<name>.+?)\s+\(T_(?<id>\d+)\)\s*$/i;

export function parseMetaTemplateFromMauticSmsName(smsName) {
  const m = META_TEMPLATE_TITLE_RE.exec(String(smsName || '').trim());
  if (!m?.groups) return null;
  const templateName = (m.groups.name || '').trim();
  const metaTemplateId = (m.groups.id || '').trim();
  if (!templateName || !metaTemplateId) return null;
  return { templateName, metaTemplateId };
}

export function isWhatsappTemplateSms(item) {
  if (!item) return false;
  const cat = String(
    item.category?.title
    || item.category?.name
    || item.category?.alias
    || item.category
    || item.channelType
    || ''
  ).toLowerCase();
  if (cat === 'whatsapp-template') return true;
  return Boolean(parseMetaTemplateFromMauticSmsName(item.name || item.smsName));
}

export async function openWhatsappTemplateInBusinessSuite({ brand, smsOrName } = {}) {
  const brandKey = String(brand || '').trim();
  if (!brandKey) {
    throw new Error('Sélectionnez une marque active pour éditer le template WhatsApp.');
  }
  const smsName = typeof smsOrName === 'string'
    ? smsOrName
    : (smsOrName?.name || smsOrName?.smsName || '');
  const parsed = parseMetaTemplateFromMauticSmsName(smsName);
  const data = await fetchWhatsappManagerUrl({
    brand: brandKey,
    templateName: parsed?.templateName,
    metaTemplateId: parsed?.metaTemplateId,
    searchText: parsed?.templateName || smsName,
  });
  if (!data?.url) {
    throw new Error(data?.message || 'URL WhatsApp Manager indisponible.');
  }
  window.open(data.url, '_blank', 'noopener,noreferrer');
  return data;
}

export async function fetchWhatsappTemplates({ brand }) {
  try {
    const params = new URLSearchParams();
    if (brand) params.set('brand', brand);
    const query = params.toString();
    const url = query
      ? `/api/inbox/whatsapp/templates?${query}`
      : '/api/inbox/whatsapp/templates';
    const resp = await inboxApi.get(url);
    return resp.data?.templates || [];
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function fetchMauticSegments() {
  try {
    const resp = await api.get('/campaigns/segments');
    if (!resp?.data?.success) {
      throw new Error(resp?.data?.message || 'Impossible de récupérer les segments Mautic.');
    }
    return Array.isArray(resp?.data?.segments) ? resp.data.segments : [];
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function fetchMauticContactsBySegment({ segmentName, segmentAlias }) {
  const name = String(segmentName || '').trim();
  const alias = String(segmentAlias || '').trim();
  if (!name && !alias) {
    throw new Error('Le segment Mautic est requis.');
  }

  try {
    // Use standard campaigns route (cookie auth) instead of manual proxy auth route.
    const resp = await api.get('/campaigns/segments/contacts', {
      params: {
        ...(name ? { segment_name: name } : {}),
        ...(alias ? { segment_alias: alias } : {}),
      },
    });
    if (!resp?.data?.success) {
      throw new Error(resp?.data?.message || 'Impossible de récupérer les contacts du segment.');
    }
    return resp?.data?.contactsPayload || {};
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function launchWhatsappTemplateCampaign({
  brand,
  templateName,
  templateLanguage,
  templateComponents,
  contactsPayload,
  scheduledAt,
}) {
  try {
    const payload = {
      brand,
      templateName,
      contactsPayload,
    };
    if (templateLanguage) payload.templateLanguage = templateLanguage;
    if (templateComponents) payload.templateComponents = templateComponents;
    if (scheduledAt) payload.scheduledAt = scheduledAt;

    const resp = await inboxApi.post('/api/inbox/whatsapp/campaign', payload);
    return resp.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

export async function fetchWhatsappCampaignHistory({ brand, limit = 20 }) {
  try {
    const params = new URLSearchParams();
    if (brand) params.set('brand', brand);
    if (limit) params.set('limit', String(limit));
    const query = params.toString();
    const url = query
      ? `/api/inbox/whatsapp/campaign/history?${query}`
      : '/api/inbox/whatsapp/campaign/history';
    const resp = await inboxApi.get(url);
    return resp.data?.history || [];
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
}

